import React, { useState, useEffect, useCallback, forwardRef } from 'react';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { useNavigate ,useLocation } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Star, CheckCircle, X as CloseIcon, ChevronRight, PlusCircle, Trash2, Save, Plus, X, ArrowRight } from 'lucide-react';

// --- CUSTOM IMPORTS ---
import usePopup from '../hooks/usePopup';
import SuccessPopup from './SuccessPopup';
import ErrorPopup from './ErrorPopup';
// --- END CUSTOM IMPORTS ---

import './GetAiReport.css';
import './MyForm.css'; // Re-use styles from MyForm
// import AILoadingPopup from './AILoadingPopup'; // ❌ REMOVED
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrochip, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import ReportConfirmationModal from './ReportConfirmationModal';


// 2. Constants
const REACT_APP_API_BASE = process.env.REACT_APP_API_BASE;
const REACT_APP_COUNSELLING_API_BASE = process.env.REACT_APP_COUNSELLING_API_BASE;


const experienceOptions = ["Fresher / No Experience", "Less than 1 year", "1-2 years", "2-5 years", "5-10 years", "10-15 years", "15-20 years", "20+ years"];
const fatherEmploymentOptions = [{ value: 'Homemaker', label: 'Homemaker' }, { value: 'Employed', label: 'Employed' }, { value: 'Self-employed / Business', label: 'Self-employed / Business' }, { value: 'Retired', label: 'Retired' }, { value: 'No longer with us', label: 'No longer with us' }];
const motherEmploymentOptions = [{ value: 'Homemaker', label: 'Homemaker' }, { value: 'Employed', label: 'Employed' }, { value: 'Self-employed / Business', label: 'Self-employed / Business' }, { value: 'Retired', label: 'Retired' }, { value: 'No longer with us', label: 'No longer with us' }];
const financialRangeOptions = [{ value: '', label: 'Select Range' }, { value: 'Below 1 LPA', label: 'Below 1 LPA' }, { value: '1 - 3 LPA', label: '1 - 3 LPA' }, { value: '3 - 5 LPA', label: '3 - 5 LPA' }, { value: '5 - 10 LPA', label: '5 - 10 LPA' }, { value: '10 - 20 LPA', label: '10 - 20 LPA' }, { value: '20 - 50 LPA', label: '20 - 50 LPA' }, { value: 'Above 50 LPA', label: 'Above 50 LPA' }];


// 3. Helper Functions / Components (Swal and showStyledAlert removed)
// Around line 30 or so:
const SelectWithLoader = forwardRef(({ label, loading, children, ...props }, ref) => (
    <div className="form-group">
        <label>{label}</label>
        <div className="pincode-group">
            <select ref={ref} {...props}>
                {children}
            </select>
            {loading && <Loader2 className="pincode-loader" />}
        </div>
    </div>
));


// 4. --- REFACTORED & MEMOIZED FORM SECTIONS --- (No changes needed in these sections)

const PersonalDetailsSection = React.memo(({ register, errors, isPincodeLoading, citiesList }) => (
    <div className="form-section">
        <h2 className="form-section-title">Personal Details</h2>
        <div className="form-grid">
            <div className="form-group"><label>First Name</label><input {...register('firstName', { required: 'First Name is required' })} type='text' placeholder="John" />{errors.firstName && <p className="form-error">{errors.firstName.message}</p>}</div>
            <div className="form-group"><label>Last Name</label><input {...register('lastName', { required: 'Last Name is required' })} placeholder="Doe" />{errors.lastName && <p className="form-error">{errors.lastName.message}</p>}</div>
            <div className="form-group"><label>Email Address</label><input {...register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+$/i, message: 'Invalid email address' } })} placeholder="user@example.com" />{errors.email && <p className="form-error">{errors.email.message}</p>}</div>
            <div className="form-group"><label>Phone Number</label><input type="tel" maxLength="10" {...register('phoneNumber', { required: 'Phone number is required', pattern: { value: /^[0-9]{10}$/, message: 'Please enter a valid 10-digit number' } })} placeholder="e.g., 9876543210" />{errors.phoneNumber && <p className="form-error">{errors.phoneNumber.message}</p>}</div>
            <div className="form-group pincode-group"><label>Pincode</label><input {...register('pincode', { required: 'Pincode is required', pattern: { value: /^[0-9]{6}$/, message: 'Must be 6 digits' } })} type="number" maxLength="6" placeholder="6-digit" />{isPincodeLoading && <Loader2 className="pincode-loader" />}{errors.pincode && <p className="form-error">{errors.pincode.message}</p>}</div>
            <div className="form-group"><label>City</label><select disabled={isPincodeLoading || citiesList.length === 0} {...register('city', { required: 'City is required' })}><option value="">{isPincodeLoading ? 'Fetching...' : (citiesList.length > 0 ? 'Select City' : 'Enter Pincode')}</option>{citiesList.map(city => <option key={city} value={city}>{city}</option>)}</select>{errors.city && <p className="form-error">{errors.city.message}</p>}</div>
            <div className="form-group"><label>District</label><input readOnly={true} {...register('district', { required: 'District is required' })} placeholder="Auto-filled" />{errors.district && <p className="form-error">{errors.district.message}</p>}</div>
            <div className="form-group"><label>State</label><input readOnly={true} {...register('state', { required: 'State is required' })} placeholder="Auto-filled" />{errors.state && <p className="form-error">{errors.state.message}</p>}</div>        </div>
    </div>
));

const AcademicBackgroundSection = React.memo(({ control, register, errors, courseFields, appendCourse, removeCourse }) => {
    const hasMoreCourses = useWatch({ control, name: 'hasMoreCourses' });

    return (
        <div className="form-section">
            <h2 className="form-section-title">Academic Background</h2>
            <div className="form-grid academic-grid">
                <div className="form-group"><label>10th Percentage</label><div className="input-with-adornment"><input type="number" step="0.01" {...register('percentage10th', { valueAsNumber: true, min: { value: 0, message: 'Cannot be negative' }, max: { value: 100, message: 'Cannot exceed 100' } })} placeholder="e.g., %" /><span className="input-adornment">%</span></div>{errors.percentage10th && <p className="form-error">{errors.percentage10th.message}</p>}</div>
                <div className="form-group"><label >12th Percentage</label><div className="input-with-adornment"><input type="number" step="0.01" {...register('percentage12th', { valueAsNumber: true, min: { value: 0, message: 'Cannot be negative' }, max: { value: 100, message: 'Cannot exceed 100' } })} placeholder="e.g., %" /><span className="input-adornment">%</span></div>{errors.percentage12th && <p className="form-error">{errors.percentage12th.message}</p>}</div>
                <div className="form-group"><label>12th Stream</label><select {...register('stream12th')}><option value="">Select</option><option>Medical</option><option>Non-Medical</option><option>Commerce</option><option>Arts</option></select></div>
                <div className="form-group"><label >College Course</label><input {...register('collegeCourse')} placeholder="e.g., B.Tec" /></div>
                <div className="form-group"><label>College Percentage</label><div className="input-with-adornment"><input type="number" step="0.01" {...register('collegePercentage', { valueAsNumber: true, min: { value: 0, message: 'Cannot be negative' }, max: { value: 100, message: 'Cannot exceed 100' } })} placeholder="e.g., %" /><span className="input-adornment">%</span></div>{errors.collegePercentage && <p className="form-error">{errors.collegePercentage.message}</p>}</div>
                <div className="form-group"><label>Any Certification</label><input {...register('certifications')} placeholder="e.g., AWS" /></div>
            </div>
            <div className="form-group-checkbox"><input type="checkbox" id="modalHasMoreCourses" {...register('hasMoreCourses')} /><label htmlFor="modalHasMoreCourses">I have additional certificates or courses to list.</label></div>
            {hasMoreCourses && (
                <div className="additional-courses-container">
                    <div className="additional-courses-header"><label>Certificate / Course Name</label><label>Marks / Percentage</label></div>
                    {courseFields.map((field, index) => (
                        <div key={field.id} className="additional-course-row">
                            <div className="form-group"><input {...register(`additionalCourses.${index}.name`, { required: "Course name is required" })} placeholder="e.g., Advanced Data Structures" />{errors.additionalCourses?.[index]?.name && <p className="form-error">{errors.additionalCourses[index].name.message}</p>}</div>
                            <div className="form-group"><div className="input-with-adornment"><input type="number" step="0.01" {...register(`additionalCourses.${index}.marks`, { valueAsNumber: true, min: { value: 0, message: 'Min 0' }, max: { value: 100, message: 'Max 100' } })} placeholder="e.g., 91.5" /><span className="input-adornment">%</span></div>{errors.additionalCourses?.[index]?.marks && <p className="form-error">{errors.additionalCourses[index].marks.message}</p>}</div>
                            <button type="button" className="form-remove-btn" onClick={() => removeCourse(index)}><X size={16} /></button>
                        </div>
                    ))}
                    <button type="button" className="form-add-btn" onClick={() => appendCourse({ name: '', marks: '' })}><Plus size={18} /> Add Another</button>
                </div>
            )}
        </div>
    );
});

const FinancialAndFamilySection = React.memo(({ control, register, errors }) => {
    const fatherEmploymentStatus = useWatch({ control, name: 'father.employmentStatus' });
    const motherEmploymentStatus = useWatch({ control, name: 'mother.employmentStatus' });
    const hasStudentJob = useWatch({ control, name: 'student.hasJob' });
    const fatherHasPension = useWatch({ control, name: 'father.hasPension' });
    const motherHasPension = useWatch({ control, name: 'mother.hasPension' });

    return (
        <div className="form-section">
            <h2 className="form-section-title">Financial & Family Details</h2>
            <div className="form-grid">
                <div className="form-group"><label>Budget</label><select {...register('budget')}><option value="">Select Budget</option><option value="Below 10 Lakhs">Below 10 Lakhs</option><option value="10 - 20 Lakhs">10 - 20 Lakhs</option><option value="20 - 30 Lakhs">20 - 30 Lakhs</option><option value="30 - 50 Lakhs">30 - 50 Lakhs</option><option value="Above 50 Lakhs">Above 50 Lakhs</option></select><p className="form-description">Your total estimated funds for the entire course.</p></div>
                <div className="form-group"><label>Bank Balance (Liquid)</label><select {...register('bankBalance.liquid')}><option value="">Select Balance</option><option value="Below 10 Lakhs">Below 10 Lakhs</option><option value="10 - 20 Lakhs">10 - 20 Lakhs</option><option value="20 - 30 Lakhs">20 - 30 Lakhs</option><option value="30 - 50 Lakhs">30 - 50 Lakhs</option><option value="50 Lakhs - 1 Cr">50 Lakhs - 1 Cr</option><option value="1 Cr - 5 Cr">1 Cr - 5 Cr</option><option value="Above 5 Cr">Above 5 Cr</option></select><p className="form-description">Money available in savings/current accounts.</p></div>
                <div className="form-group"><label>Bank Balance (Fixed Deposit)</label><select {...register('bankBalance.fd')}><option value="">Select Balance</option><option value="Below 10 Lakhs">Below 10 Lakhs</option><option value="10 - 20 Lakhs">10 - 20 Lakhs</option><option value="20 - 30 Lakhs">20 - 30 Lakhs</option><option value="30 - 50 Lakhs">30 - 50 Lakhs</option><option value="50 Lakhs - 1 Cr">50 Lakhs - 1 Cr</option><option value="1 Cr - 5 Cr">1 Cr - 5 Cr</option><option value="Above 5 Cr">Above 5 Cr</option></select><p className="form-description">Money held in Fixed Deposits (FDs).</p></div>
            </div>

            <h3 className="form-section-subtitle">Father's Details</h3>
            <div className="employment-status-group"><span className="radio-group-label">Employment Status</span><div role="radiogroup" className="radio-group">{fatherEmploymentOptions.map(opt => (<label key={`father-modal-${opt.value}`} className={`radio-label ${fatherEmploymentStatus === opt.value ? 'selected' : ''}`}><input type="radio" {...register('father.employmentStatus')} value={opt.value} />{opt.label}</label>))}</div></div>
            {(fatherEmploymentStatus === 'Employed' || fatherEmploymentStatus === 'Self-employed / Business') && (
                <div className="conditional-fields-wrapper"><div className="form-grid">
                    {fatherEmploymentStatus === 'Employed' && (<><div className="form-group"><label>Experience</label><select {...register('father.experience')}><option value="">Select Experience</option>{experienceOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div><div className="form-group"><label>Designation</label><input {...register('father.designation')} placeholder="e.g., Senior Manager" /></div><div className="form-group"><label>Salary (Yearly)</label><select {...register('father.salary')}>{financialRangeOptions.map(opt => <option key={`father-salary-${opt.value}`} value={opt.value}>{opt.label}</option>)}</select></div></>)}
                    {fatherEmploymentStatus === 'Self-employed / Business' && (<><div className="form-group"><label>Type of Business</label><input {...register('father.businessType')} placeholder="e.g., Retail, Manufacturing" /></div><div className="form-group"><label>Yearly Revenue</label><select {...register('father.revenue')}>{financialRangeOptions.map(opt => <option key={`father-revenue-${opt.value}`} value={opt.value}>{opt.label}</option>)}</select></div></>)}
                </div></div>
            )}
            {fatherEmploymentStatus === 'Retired' && (
                <div className="conditional-fields-wrapper">
                    <div className="form-group-checkbox pension-checkbox"><input type="checkbox" id="fatherHasPension-modal" {...register('father.hasPension')} /><label htmlFor="fatherHasPension-modal">Receives a pension</label></div>
                    {fatherHasPension && (<div className="form-grid"><div className="form-group"><label>Pension Amount (Yearly)</label><select {...register('father.pensionAmount')}>{financialRangeOptions.map(opt => <option key={`father-pension-${opt.value}`} value={opt.value}>{opt.label}</option>)}</select></div></div>)}
                </div>
            )}

            <h3 className="form-section-subtitle">Mother's Details</h3>
            <div className="employment-status-group"><span className="radio-group-label">Employment Status</span><div role="radiogroup" className="radio-group">{motherEmploymentOptions.map(opt => (<label key={`mother-modal-${opt.value}`} className={`radio-label ${motherEmploymentStatus === opt.value ? 'selected' : ''}`}><input type="radio" {...register('mother.employmentStatus')} value={opt.value} />{opt.label}</label>))}</div></div>
            {(motherEmploymentStatus === 'Employed' || motherEmploymentStatus === 'Self-employed / Business') && (
                <div className="conditional-fields-wrapper"><div className="form-grid">
                    {motherEmploymentStatus === 'Employed' && (<><div className="form-group"><label>Experience</label><select {...register('mother.experience')}><option value="">Select Experience</option>{experienceOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div><div className="form-group"><label>Designation</label><input {...register('mother.designation')} placeholder="e.g., Teacher" /></div><div className="form-group"><label>Salary (Yearly)</label><select {...register('mother.salary')}>{financialRangeOptions.map(opt => <option key={`mother-salary-${opt.value}`} value={opt.value}>{opt.label}</option>)}</select></div></>)}
                    {motherEmploymentStatus === 'Self-employed / Business' && (<><div className="form-group"><label>Type of Business</label><input {...register('mother.businessType')} placeholder="e.g., Boutique, Consulting" /></div><div className="form-group"><label>Yearly Revenue</label><select {...register('mother.revenue')}>{financialRangeOptions.map(opt => <option key={`mother-revenue-${opt.value}`} value={opt.value}>{opt.label}</option>)}</select></div></>)}
                </div></div>
            )}
            {motherEmploymentStatus === 'Retired' && (
                <div className="conditional-fields-wrapper">
                    <div className="form-group-checkbox pension-checkbox"><input type="checkbox" id="motherHasPension-modal" {...register('mother.hasPension')} /><label htmlFor="motherHasPension-modal">Receives a pension</label></div>
                    {motherHasPension && (<div className="form-grid"><div className="form-group"><label>Pension Amount (Yearly)</label><select {...register('mother.pensionAmount')}>{financialRangeOptions.map(opt => <option key={`mother-pension-${opt.value}`} value={opt.value}>{opt.label}</option>)}</select></div></div>)}
                </div>
            )}

            <h3 className="form-section-subtitle">Student's Work Experience</h3>
            <div className="form-group-checkbox"><input type="checkbox" id="hasStudentJob-modal" {...register('student.hasJob')} /><label htmlFor="hasStudentJob-modal">I have professional work experience.</label></div>
            {hasStudentJob && (
                <div className="conditional-fields-wrapper"><div className="form-grid">
                    <div className="form-group"><label>Job Title / Designation</label><input {...register('student.jobTitle')} placeholder="e.g., Software Engineer" /></div>
                    <div className="form-group"><label>Company Name</label><input {...register('student.companyName')} placeholder="e.g., Tech Solutions Inc." /></div>
                    <div className="form-group"><label>Years of Experience</label><select {...register('student.experience')}><option value="">Select Experience</option>{experienceOptions.map(opt => <option key={`student-exp-${opt}`} value={opt}>{opt}</option>)}</select></div>
                    <div className="form-group"><label>Salary (Yearly)</label><select {...register('student.salary')}>{financialRangeOptions.map(opt => <option key={`student-salary-${opt.value}`} value={opt.value}>{opt.label}</option>)}</select></div>
                </div></div>
            )}
        </div>
    );
});

const StudentProfileForm = ({ formMethods }) => (
    <div className="student-form-in-modal student-form">
        <PersonalDetailsSection {...formMethods} />
        <AcademicBackgroundSection {...formMethods} />
        <FinancialAndFamilySection {...formMethods} />
    </div>
);

const IELTSScoreForm = React.memo(({ control, register, setValue, errors }) => {
    const bandScores = useWatch({
        control,
        name: ['ielts.listening', 'ielts.reading', 'ielts.writing', 'ielts.speaking'],
    });
    const [listening, reading, writing, speaking] = bandScores;
    useEffect(() => {
        const scores = [listening, reading, writing, speaking].map(s => s ? parseFloat(s) : NaN);
        const validScores = scores.filter(s => !isNaN(s));
        if (validScores.length === 4) {
            const average = validScores.reduce((sum, score) => sum + score, 0) / 4;
            const decimal = average - Math.floor(average);
            let overall = (decimal >= 0.25 && decimal < 0.75) ? Math.floor(average) + 0.5 : Math.round(average);
            setValue('ielts.overall', overall.toFixed(1), { shouldValidate: true });
        } else {
            setValue('ielts.overall', '', { shouldValidate: true });
        }
    }, [listening, reading, writing, speaking, setValue]);
    const bandOptions = [<option key="select" value="">Select</option>, ...Array.from({ length: 19 }, (_, i) => (i / 2).toFixed(1)).map(val => (<option key={val} value={val}>{val}</option>))];

    // The changes are in the return statement below
    return (
        <div className="ielts-breakdown-grid">
            <div className="form-group"><label>Listening</label><select {...register('ielts.listening', { required: "Score required" })}>{bandOptions}</select>{errors.ielts?.listening && <p className="form-error">{errors.ielts.listening.message}</p>}</div>
            <div className="form-group"><label>Reading</label><select {...register('ielts.reading', { required: "Score required" })}>{bandOptions}</select>{errors.ielts?.reading && <p className="form-error">{errors.ielts.reading.message}</p>}</div>
            <div className="form-group"><label>Writing</label><select {...register('ielts.writing', { required: "Score required" })}>{bandOptions}</select>{errors.ielts?.writing && <p className="form-error">{errors.ielts.writing.message}</p>}</div>
            <div className="form-group"><label>Speaking</label><select {...register('ielts.speaking', { required: "Score required" })}>{bandOptions}</select>{errors.ielts?.speaking && <p className="form-error">{errors.ielts.speaking.message}</p>}</div>

            {/* ▼▼▼ CLASS ADDED HERE ▼▼▼ */}
            <div className="form-group overall-score full-width-grid-item">
                <label>Overall Band</label>
                <input readOnly {...register('ielts.overall')} placeholder="Auto" />
                {errors.ielts?.overall && <p className="form-error">{errors.ielts.overall.message}</p>}
            </div>
        </div>
    );
});

const EditableGoalsSection = React.memo(({ selectedPackage, control, register, setValue, errors, countriesList, statesList, locationLoading, examFields, appendExam, removeExam }) => {
    const goalCountry = useWatch({ control, name: 'goalCountry' });
    const hasOtherScore = useWatch({ control, name: 'hasOtherScore' });
    const testType = useWatch({ control, name: 'testType', defaultValue: 'ielts' });

    if (!selectedPackage) return null;

    const { packageId } = selectedPackage;
    const showState = packageId === 'PKG001' || packageId === 'PKG002';
    const showUni = packageId === 'PKG001';

    return (
        <div className="review-goals-section">
            <h4 className="review-goals-title">Review and Edit Goals & Scores for this Report:</h4>
            <div className="form-grid modal-form-grid">
                <div><SelectWithLoader label="Goal Country" {...register('goalCountry', { required: 'Country is required' })}><option value="">-- Select Country --</option>{countriesList.map(c => <option key={c} value={c}>{c}</option>)}</SelectWithLoader>{errors.goalCountry && <p className="form-error">{errors.goalCountry.message}</p>}</div>
                {showState && (<div><SelectWithLoader label="Goal State" loading={locationLoading.states} disabled={!goalCountry} {...register('goalState', { required: showState ? 'State is required' : false })}><option value="">-- Select State --</option>{statesList.map(s => <option key={s} value={s}>{s}</option>)}</SelectWithLoader>{errors.goalState && <p className="form-error">{errors.goalState.message}</p>}</div>)}
                {showUni && (<div><div className="form-group"><label>Goal University/College</label><input {...register('selectedCollege', { required: showUni ? 'University is required' : false })} placeholder="e.g., University of Toronto" /></div>{errors.selectedCollege && <p className="form-error">{errors.selectedCollege.message}</p>}</div>)}
            </div>

            <div className="form-group test-type-selector">
                <label>Proficiency Test</label>
                <div className="radio-group">
                    <label className={`radio-label ${testType === 'ielts' ? 'selected' : ''}`}><input type="radio" {...register('testType')} value="ielts" /> IELTS</label>
                    <label className={`radio-label ${testType === 'toefl' ? 'selected' : ''}`}><input type="radio" {...register('testType')} value="toefl" /> TOEFL</label>
                    <label className={`radio-label ${testType === 'pte' ? 'selected' : ''}`}><input type="radio" {...register('testType')} value="pte" /> PTE</label>
                </div>
            </div>

            {testType === 'ielts' && (<div className="test-score-section"><h5 className="test-score-title">IELTS Band Scores</h5><IELTSScoreForm control={control} register={register} setValue={setValue} errors={errors} /></div>)}
            {testType === 'toefl' && (
                <div className="test-score-section">
                    <div className="form-group">
                        <label>TOEFL Score (out of 120)</label>
                        <input type="number" {...register('toeflScore', { required: 'TOEFL score is required', min: { value: 0, message: 'Min 0' }, max: { value: 120, message: 'Max 120' } })} placeholder="e.g., 100" />
                        {errors.toeflScore && <p className="form-error">{errors.toeflScore.message}</p>}
                    </div>
                </div>
            )}
            {testType === 'pte' && (
                <div className="test-score-section">
                    <div className="form-group">
                        <label>PTE Score (10-90)</label>
                        <input type="number" {...register('pteScore', { required: 'PTE score is required', min: { value: 10, message: 'Min 10' }, max: { value: 90, message: 'Max 90' } })} placeholder="e.g., 79" />
                        {errors.pteScore && <p className="form-error">{errors.pteScore.message}</p>}
                    </div>
                </div>
            )}

            <div className="checkbox-group full-width modal-checkbox"><input type="checkbox" {...register('hasOtherScore')} id="modalHasOtherScore" /><label htmlFor="modalHasOtherScore">Do you have any other test score?</label></div>
            {hasOtherScore && (
                <div className="other-exams-container">
                    {examFields.map((item, index) => (
                        <div key={item.id} className="dynamic-exam-row">
                            <div className="form-group dynamic-field"><label>Other Exam Name</label><input {...register(`otherExams.${index}.name`, { required: 'Exam name is required' })} placeholder="e.g., GRE, GMAT" />{errors.otherExams?.[index]?.name && <p className="form-error">{errors.otherExams[index].name.message}</p>}</div>
                            <div className="form-group dynamic-field"><label>Score</label><input {...register(`otherExams.${index}.score`, { required: 'Score is required' })} placeholder="e.g., 320" />{errors.otherExams?.[index]?.score && <p className="form-error">{errors.otherExams[index].score.message}</p>}</div>
                            <button type="button" className="remove-exam-btn" onClick={() => removeExam(index)}><Trash2 size={18} /></button>
                        </div>
                    ))}
                    <button type="button" className="add-exam-btn" onClick={() => appendExam({ name: '', score: '' })}><PlusCircle size={16} /> Add Another Exam</button>
                </div>
            )}
        </div>
    );
});

const MultiStepPackageModal = ({ show, onCancel, onConfirm, isPaying, modalStep, setModalStep, tempPackage, packages, formMethods, onUpdateForm, isSubmittingForm }) => {
    const [viewMode, setViewMode] = useState('disclaimer');
    useEffect(() => { if (show) { setViewMode('disclaimer'); } }, [show]);

    if (!show) return null;
    const selectedPackage = packages.find(p => p.price.toString() === tempPackage);

    return (
        <div className="modal-overlay">
            <div className="modal-content large">
                <button onClick={onCancel} className="modal-close-btn"><CloseIcon size={20} /></button>
                <div className="stepper-container">
                    {[1, 2, 3].map((num, idx) => (<React.Fragment key={num}><div className={`step-circle ${modalStep >= num ? 'active' : ''}`}>{num}</div>{idx < 2 && <div className={`step-line ${modalStep > num ? 'active' : ''}`} />}</React.Fragment>))}
                </div>
                {modalStep === 1 && (
                    <div className="step-content">
                        <h3 className="modal-title">Step 1: Review Your Profile</h3>
                        {viewMode === 'disclaimer' ? (
                            <div className="disclaimer-view" style={{ padding: '30px 0' }}>
                                <p className="modal-subtitle" style={{ textAlign: 'center', marginBottom: '2rem' }}>Your report's accuracy depends on up-to-date information. Please review or edit your profile.</p>
                                <div className="step-nav-buttons" style={{ justifyContent: 'center', gap: '1rem' }}>
                                    <button type="button" className="nav-button nav-button-primary" onClick={() => setViewMode('form')}>Edit Form</button>
                                    <button type="button" className="nav-button nav-button-secondary" onClick={() => setModalStep(2)}>Next</button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <p className="modal-subtitle">Ensure your details are up-to-date for the most accurate report. Changes will be saved for future use.</p>
                                <form id="profile-update-form" className="modal-form-container" onSubmit={onUpdateForm}>
                                    <StudentProfileForm formMethods={formMethods} />
                                </form>
                                <div className="step-nav-buttons modal-step-1-nav">
                                    <button type="submit" form="profile-update-form" className="nav-button nav-button-save" disabled={isSubmittingForm}>{isSubmittingForm ? <><Loader2 className="animate-spin" /> Saving...</> : <><Save size={16} /> Save Changes</>}</button>
                                    <div className="modal-step-1-right-buttons">
                                        <button type="button" className="nav-button nav-button-secondary" onClick={() => setViewMode('disclaimer')}>Back</button>
                                        <button type="button" className="nav-button nav-button-primary" onClick={() => formMethods.trigger().then(isValid => isValid && setModalStep(2))}>Next <ChevronRight size={16} /></button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
                {modalStep === 2 && (
                    <div className="step-content">
                        <h3 className="modal-title">Step 2: Review & Confirm</h3>
                        {selectedPackage && (
                            <>
                                <div className="review-details"><div><strong>Package:</strong> {selectedPackage.name}</div><div><strong>Price:</strong> ₹{selectedPackage.price}</div></div>
                                <EditableGoalsSection selectedPackage={selectedPackage} {...formMethods} />
                            </>
                        )}
                        <div className="step-nav-buttons">
                            <button className="nav-button nav-button-secondary" onClick={() => setModalStep(1)}>Back</button>
                            <button className="nav-button nav-button-primary" disabled={isPaying} onClick={onConfirm}>{isPaying ? <><Loader2 className="animate-spin" /> Processing...</> : `Confirm & Proceed`}</button>
                        </div>
                    </div>
                )}
                {modalStep === 3 && (<div className="step-content"><h3 className="modal-title">Step 3: Processing Payment</h3><div className="flex-center"><Loader2 size={40} className="animate-spin" /><p>Redirecting to payment gateway...</p></div></div>)}
            </div>
        </div>
    );
};


// --- 5. MAIN COMPONENT ---
const GetAiReport = () => {
    const navigate = useNavigate();
    const userId = localStorage.getItem('userId');

    // --- NEW: USE POPUP HOOK ---
    const { popupState, showSuccessPopup, showErrorPopup, closePopup } = usePopup();

    const { register, watch, setValue, reset, getValues, trigger, control, handleSubmit, formState: { errors } } = useForm({
        mode: 'onTouched',
        defaultValues: {
            ielts: { listening: '', reading: '', writing: '', speaking: '', overall: '' },
            toeflScore: '',
            pteScore: '',
            testType: 'ielts',
            otherExams: [],
            additionalCourses: [],
        }
    });

    const { fields: examFields, append: appendExam, remove: removeExam } = useFieldArray({ control, name: "otherExams" });
    const { fields: courseFields, append: appendCourse, remove: removeCourse } = useFieldArray({ control, name: "additionalCourses" });

    const pincode = watch('pincode');
    const goalCountry = watch('goalCountry');
    const hasOtherScore = watch('hasOtherScore');
    const hasMoreCourses = watch('hasMoreCourses');

    const [packages, setPackages] = useState([]);
    const [baseFormData, setBaseFormData] = useState(null);
    const [countriesList, setCountriesList] = useState([]);
    const [statesList, setStatesList] = useState([]);
    const [citiesList, setCitiesList] = useState([]);
    const [locationLoading, setLocationLoading] = useState({ states: false });
    const [isPincodeLoading, setIsPincodeLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showPackageModal, setShowPackageModal] = useState(false);
    const [isPaying, setIsPaying] = useState(false);
    const [isSubmittingForm, setIsSubmittingForm] = useState(false);
    const [modalStep, setModalStep] = useState(1);
    const [tempPackage, setTempPackage] = useState('');
    // const [isAiGenerating, setIsAiGenerating] = useState(false); // ❌ REMOVED
    const [showReportModal, setShowReportModal] = useState(false);
    const [selectedPackageForPayment, setSelectedPackageForPayment] = useState(null);
    const [couponError, setCouponError] = useState('');
    const [couponLoading, setCouponLoading] = useState(false);
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [priceDetails, setPriceDetails] = useState({ original: 0, discount: 0, gst: 0, final: 0 });
    const [reportId, setReportId] = useState(null);


      const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

    useEffect(() => {
        const script = document.createElement('script');
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.async = true;
        document.body.appendChild(script);

        const fetchInitialData = async () => {
            if (!userId) {
                setError("You must be logged in to view this page.");
                setIsLoading(false);
                return;
            }
            try {
                const [packagesRes, formRes, countriesRes] = await Promise.all([
                    axios.get(`${REACT_APP_API_BASE}/api/packages`),
                    axios.get(`${REACT_APP_API_BASE}/api/form/${userId}`),
                    axios.get('https://countriesnow.space/api/v0.1/countries/positions')
                ]);

                setPackages([...packagesRes.data].sort((a, b) => a.price - b.price));
                setCountriesList(countriesRes.data.data.map(c => c.name).sort());

                if (formRes.data && Object.keys(formRes.data).length > 0) {
                    const formData = {
                        ...formRes.data,
                        testType: formRes.data.testType || 'ielts',
                        ielts: formRes.data.ielts || { listening: '', reading: '', writing: '', speaking: '', overall: '' },
                        toeflScore: formRes.data.toeflScore || '',
                        pteScore: formRes.data.pteScore || '',
                        otherExams: formRes.data.otherExams || [],
                        additionalCourses: formRes.data.additionalCourses || []
                    };
                    setBaseFormData(formData);
                    reset(formData);
                } else {
                    setError("Your profile form is not complete. Please fill it out before generating a report.");
                }
            } catch (err) {
                console.error("Failed to fetch initial data:", err);
                setError("Could not load required data. Please ensure your form is saved, then try again.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchInitialData();
        return () => { document.body.removeChild(script); };
    }, [userId, reset]);

    useEffect(() => {
        if (!goalCountry) { setStatesList([]); return; }
        let isMounted = true;
        (async () => {
            setLocationLoading(p => ({ ...p, states: true }));
            setValue('goalState', '');
            try {
                const { data } = await axios.post('https://countriesnow.space/api/v0.1/countries/states', { country: goalCountry });
                if (isMounted) setStatesList(data.data.states.map(s => s.name).sort());
            } catch { if (isMounted) setStatesList([]); }
            finally { if (isMounted) setLocationLoading(p => ({ ...p, states: false })); }
        })();
        return () => { isMounted = false };
    }, [goalCountry, setValue]);

    useEffect(() => {
        if (!pincode || String(pincode).length !== 6) { setCitiesList([]); return; }
        const timer = setTimeout(async () => {
            setIsPincodeLoading(true);
            try {
                const { data } = await axios.get(`https://api.postalpincode.in/pincode/${pincode}`);
                if (data[0].Status === 'Success') {
                    const postOffices = data[0].PostOffice;
                    setCitiesList([...new Set(postOffices.map(po => po.Name))].sort());
                    setValue('district', postOffices[0].District, { shouldValidate: true });
                    setValue('state', postOffices[0].State, { shouldValidate: true });
                } else { setCitiesList([]); }
            } catch { setCitiesList([]); }
            finally { setIsPincodeLoading(false); }
        }, 500);
        return () => clearTimeout(timer);
    }, [pincode, setValue]);

    useEffect(() => {
        if (hasOtherScore && examFields.length === 0) { appendExam({ name: '', score: '' }); }
        else if (!hasOtherScore && examFields.length > 0) { removeExam(); }
    }, [hasOtherScore, examFields.length, appendExam, removeExam]);

    useEffect(() => {
        if (hasMoreCourses && courseFields.length === 0) { appendCourse({ name: '', marks: '' }); }
        else if (!hasMoreCourses && courseFields.length > 0) { removeCourse(); }
    }, [hasMoreCourses, courseFields.length, appendCourse, removeCourse]);


    const handleRemoveCoupon = useCallback(() => {
        if (!selectedPackageForPayment) return;
        setAppliedCoupon(null);
        setCouponError('');
        const basePrice = selectedPackageForPayment.price;
        const gst = basePrice * 0.18;
        setPriceDetails({ original: basePrice, discount: 0, gst, final: basePrice + gst });
    }, [selectedPackageForPayment]);

const handleApplyCoupon = useCallback(async (codeToApply) => {
    const trimCode = (codeToApply || '').trim();
    if (!trimCode || !selectedPackageForPayment) return;

    // get userId robustly from localStorage
    const getUserIdFromStorage = () => {
        const direct = localStorage.getItem('userId');
        if (direct) return direct;
        try {
            const cu = JSON.parse(localStorage.getItem('currentUser') || '{}');
            return cu?.userId || cu?._id || cu?.id || null;
        } catch (e) {
            return null;
        }
    };

    const userId = getUserIdFromStorage();
    if (!userId) {
        const msg = 'You must be logged in to apply a coupon.';
        setCouponError(msg);
        showErrorPopup({ title: 'Coupon Error', message: msg });
        return;
    }

    setCouponLoading(true);
    setCouponError('');
    try {
        // optional: include auth token if available
        const token = localStorage.getItem('token') || (() => {
            try {
                const cu = JSON.parse(localStorage.getItem('currentUser') || '{}');
                return cu?.token || '';
            } catch (e) {
                return '';
            }
        })();

        const payload = {
            code: trimCode,
            serviceType: 'report',
            packageId: selectedPackageForPayment._id,
            userId, // <-- required by backend
        };

        const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

        const response = await axios.post(
            `${REACT_APP_COUNSELLING_API_BASE}/api/coupons/validate`,
            payload,
            config
        );

        const coupon = response.data;
        setAppliedCoupon(coupon);

        const basePrice = selectedPackageForPayment.price;
        let discount = 0;
        if (coupon.discountType === 'percentage') discount = (basePrice * coupon.discountValue) / 100;
        else if (coupon.discountType === 'fixed') discount = coupon.discountValue;

        const discountedPrice = Math.max(0, basePrice - discount);
        const gst = Number((discountedPrice * 0.18).toFixed(2));
        const finalPrice = Number((discountedPrice + gst).toFixed(2));

        setPriceDetails({ original: basePrice, discount, gst, final: finalPrice });
        setCouponError('');
    } catch (error) {
        const errorMessage = error.response?.data?.message || "Could not apply coupon.";
        setCouponError(errorMessage);
        showErrorPopup({
            title: 'Coupon Error',
            message: errorMessage,
        });
        handleRemoveCoupon();
    } finally {
        setCouponLoading(false);
    }
}, [selectedPackageForPayment, handleRemoveCoupon, showErrorPopup]);



    const onUpdateFormSubmit = useCallback(async (data) => {
        setIsSubmittingForm(true);
        try {
            await axios.post(`${REACT_APP_API_BASE}/api/form/submit`, { ...data, userId });
            const updatedData = { ...data };
            setBaseFormData(updatedData);
            reset(updatedData);
            // REPLACED
            showSuccessPopup({ title: 'Profile Updated!', message: 'Your changes have been saved.', timer: 2000 });
        } catch (err) {
            // REPLACED
            showErrorPopup({ title: 'Update Failed', message: err.response?.data?.message || 'An error occurred while saving your profile.' });
        } finally {
            setIsSubmittingForm(false);
        }
    }, [userId, reset, showSuccessPopup, showErrorPopup]);

    // =========================================================================
    // ★★★ MODIFIED PAYMENT & REDIRECTION LOGIC ★★★
    // =========================================================================
    const handlePayment = useCallback(async () => {
        setIsPaying(true); // Shows spinner on the button
        setShowReportModal(false); // Close the confirmation modal

        const allValues = getValues();
        const payloadFormData = {
            firstName: allValues.firstName, lastName: allValues.lastName, email: allValues.email, phoneNumber: allValues.phoneNumber,
            pincode: allValues.pincode, city: allValues.city, district: allValues.district, state: allValues.state,
            country: allValues.country || "India", percentage10th: allValues.percentage10th, percentage12th: allValues.percentage12th,
            stream12th: allValues.stream12th, collegeCourse: allValues.collegeCourse, collegePercentage: allValues.collegePercentage,
            certifications: allValues.certifications, additionalCourses: allValues.hasMoreCourses ? allValues.additionalCourses : [],
            budget: allValues.budget, bankBalance: allValues.bankBalance, father: allValues.father, mother: allValues.mother, student: allValues.student,
            goalCountry: allValues.goalCountry, goalState: allValues.goalState, selectedCollege: allValues.selectedCollege,
            testType: allValues.testType, ieltsScore: allValues.testType === "ielts" ? allValues.ielts.overall : null,
            toeflScore: allValues.testType === "toefl" ? allValues.toeflScore : null, pteScore: allValues.testType === "pte" ? allValues.pteScore : null,
            otherExams: allValues.hasOtherScore ? allValues.otherExams : []
        };

        const orderPayload = {
            amount: selectedPackageForPayment.price,
            packageType: selectedPackageForPayment.name,
            packageId: selectedPackageForPayment._id,
            userId,
            formData: payloadFormData,
            couponCode: appliedCoupon?.code ?? null,
        };

        // ▼▼▼ MODIFICATION HERE ▼▼▼
        // Helper function to determine the correct redirection URL based on the purchased package.
        const getRedirectUrl = (reportId) => {
            const pkgName = (selectedPackageForPayment?.name || '').toLowerCase();

            if (pkgName.includes('advance')) {
                // ₹249 “Advance” → plus‐processing
                return `/dashboard?view=report-plus-processing&id=${reportId}`;
            }
            else if (pkgName.includes('smart')) {
                return `/dashboard?view=report-premium-processing&id=${reportId}`;
            }
            // everything else (₹99 etc.) → standard processing
            return `/dashboard?view=report-processing&id=${reportId}`;
        };

        try {
            // --- FREE & PAID FLOWS (NOW ASYNCHRONOUS) ---
            if (priceDetails.final <= 0) {
                // Free flow: create order, get reportId, and redirect to the appropriate processing page
                const { data: orderData } = await axios.post(
                    `${REACT_APP_API_BASE}/form-create-order`,
                    orderPayload
                );
                navigate(getRedirectUrl(orderData.reportId));

            } else {
                // Paid flow: create order, open Razorpay
                const { data: orderData } = await axios.post(
                    `${REACT_APP_API_BASE}/form-create-order`,
                    orderPayload
                );



                const rzp = new window.Razorpay({
                    key: orderData.key,
                    amount: orderData.amount,
                    order_id: orderData.orderId,
                    name: "Get Next Visa",
                    description: `AI-Powered Visa Report`,
                    image: "/photos/newlogo.png",
                    handler: async (response) => {
                        // After successful payment, verify it on the backend.
                        try {
                            const { data: verifyData } = await axios.post(
                                `${REACT_APP_API_BASE}/form-verify`,
                                response
                            );
                            navigate(getRedirectUrl(verifyData.reportId));
                        } catch (err) {
                            showErrorPopup({ title: "Process Failed", message: err.response?.data?.error || err.response?.data?.message || "An error occurred after payment." });
                            setIsPaying(false);
                        }
                    },
                    modal: { ondismiss: () => { setIsPaying(false); } },
                    prefill: {
                        name: `${payloadFormData.firstName} ${payloadFormData.lastName}`,
                        email: payloadFormData.email,
                        contact: payloadFormData.phoneNumber
                    },
                    theme: { color: "#003366" },
                });

                // ★★★ THIS IS THE FINAL, CORRECTED FAILURE HANDLER ★★★
                rzp.on("payment.failed", async (err) => {
                    // 1. Show the user a friendly error message immediately.
                    showErrorPopup({ title: "Payment Failed", message: err.error.description });
                    setIsPaying(false);

                    // 2. Prepare the data payload for your backend.
                    const failureDetails = {
                        orderId: err.error.metadata.order_id,
                        paymentId: err.error.metadata.payment_id,
                        reason: err.error.description,
                        email: payloadFormData.email, // Get the email from the form data.
                    };

                    // 3. Log the data to your browser's console for debugging.
                    //    PRESS F12 in your browser to see this log.
                    console.log("SENDING FAILURE REPORT TO BACKEND:", failureDetails);

                    // 4. Send the failure report to your server.
                    try {
                        await axios.post(`${REACT_APP_API_BASE}/api/payment-failed`, failureDetails);
                        console.log("Backend has been successfully notified of the payment failure.");
                    } catch (apiError) {
                        console.error("CRITICAL: The API call to /api/payment-failed failed:", apiError.message);
                    }
                });

                rzp.open();
                // setIsPaying(false); 
            }
        } catch (err) {
            showErrorPopup({ title: "Order Creation Error", message: err.response?.data?.error || err.response?.data?.message || "Could not connect to the server." });
            setIsPaying(false);
        }
    }, [
        getValues, selectedPackageForPayment, priceDetails.final, userId, navigate,
        appliedCoupon, showErrorPopup
    ]);

    const handleChoosePlanClick = useCallback((pkg) => {
        reset(baseFormData);
        setTempPackage(pkg.price.toString());
        setModalStep(1);
        setShowPackageModal(true);
    }, [baseFormData, reset]);



    const handleCancelModal = useCallback(() => {
        setShowPackageModal(false);
        setModalStep(1);
        reset(baseFormData);
    }, [baseFormData, reset]);

    const handleOpenPaymentModal = useCallback(async () => {
        const isValid = await trigger();
        if (!isValid) {
            showErrorPopup({ title: 'Incomplete Form', message: 'Please fill all required fields in Step 2 before proceeding.' });
            return;
        }

        setShowPackageModal(false);
        const pkg = packages.find(p => p.price.toString() === tempPackage);
        if (!pkg) {
            showErrorPopup({ title: 'Package Error', message: 'Could not find the selected package. Please try again.' });
            return;
        }
        setSelectedPackageForPayment(pkg);

        const basePrice = pkg.price;
        const gst = basePrice * 0.18;
        setPriceDetails({ original: basePrice, discount: 0, gst, final: basePrice + gst });
        setAppliedCoupon(null);
        setCouponError('');

        setShowReportModal(true);
    }, [packages, tempPackage, trigger, showErrorPopup]);

    const renderContent = () => {
        if (isLoading) return (<div className="report-loading-container"><Loader2 className="animate-spin" size={48} /><p>Loading AI Packages...</p></div>);
        if (error) return (<div className="report-error-container"><p>{error}</p><button className="nav-button nav-button-primary" onClick={() => navigate('/dashboard?view=form')}>Go to My Form</button></div>);
        return (
            <div className="packages-grid">
                {packages.map(pkg => (
                    <div key={pkg._id} className="package-card-report">
                        <div className="package-header-report">
                            <span className="package-name-report">{pkg.name}</span>
                            {pkg.isPopular && <span className="popular-badge-report"><Star size={12} /> Popular</span>}
                        </div>
                        
                        {pkg.description && <p className="package-description-report">{pkg.description}</p>}

                        <div className="package-price-report">
                            <div>
                                <span className="price-amount">
                                    {/* ▼▼▼ MODIFICATION IS HERE ▼▼▼ */}
                                    {pkg.price === 0 ? ' Free' : `₹${pkg.price}`}
                                </span>
                                <span className="price-term"></span>
                            </div>
                            <button className="start-now-btn" onClick={() => handleChoosePlanClick(pkg)}>
                                Start Now <FontAwesomeIcon icon={faArrowRight} />
                            </button>
                        </div>
                        <ul className="package-features-report">
                            {pkg.features.map((feature, i) => (
                                <li key={i}>
                                    <CheckCircle size={16} className="feature-icon" /> {feature}
                                </li>
                            ))}
                        </ul>
                        <button className="choose-plan-btn" onClick={() => handleChoosePlanClick(pkg)}>
                            Choose Plan <ChevronRight size={16} />
                        </button>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="get-ai-report-container">
            <SuccessPopup
                isOpen={popupState.isOpen && popupState.type === 'success'}
                onClose={closePopup}
                title={popupState.title}
                message={popupState.message}
                timer={popupState.timer}
            />
            <ErrorPopup
                isOpen={popupState.isOpen && popupState.type === 'error'}
                onClose={closePopup}
                title={popupState.title}
                message={popupState.message}
            />

            <MultiStepPackageModal
                show={showPackageModal}
                onCancel={handleCancelModal}
                onConfirm={handleOpenPaymentModal}
                onUpdateForm={handleSubmit(onUpdateFormSubmit)}
                isPaying={isPaying}
                isSubmittingForm={isSubmittingForm}
                modalStep={modalStep}
                setModalStep={setModalStep}
                tempPackage={tempPackage}
                packages={packages}
                formMethods={{
                    register, control, errors, setValue, getValues, trigger,
                    countriesList, statesList, citiesList, locationLoading, isPincodeLoading,
                    examFields, appendExam, removeExam, courseFields, appendCourse, removeCourse
                }}
            />

            <ReportConfirmationModal
                isOpen={showReportModal}
                onClose={() => {
                    setShowReportModal(false);
                    setIsPaying(false);
                }}
                selectedPackage={selectedPackageForPayment}
                initiatePayment={handlePayment}
                isPaying={isPaying}
                handleApplyCoupon={handleApplyCoupon}
                handleRemoveCoupon={handleRemoveCoupon}
                appliedCoupon={appliedCoupon}
                couponError={couponError}
                couponLoading={couponLoading}
                priceDetails={priceDetails}
            />

            <div className="report-header-1">
                <FontAwesomeIcon className="ai-report-icon" icon={faMicrochip} size="2x" />
                <div>
                    <h1 className="report-title">Get Your AI-Powered Immigration Report</h1>
                    <p className="report-subtitle">Choose a package below to get a detailed analysis based on your profile.</p>
                </div>
            </div>

            {renderContent()}
        </div>
    );
};
export default GetAiReport;